import "./AllReportRun.scss";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest";
import { ScaleLoader } from "react-spinners";
import { FiSearch } from "react-icons/fi";
import { ArrowLeft, Home } from "lucide-react";
import { MdAlternateEmail, MdArrowBackIos, MdClose, MdDownload } from "react-icons/md";
import { qilloIconMap } from "../../../utils/iconMapping";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import { GoArrowRight } from "react-icons/go";
import { CustomRadioGroup } from "../../../components/radio/CustomRadioGroup";
import { isValidMobileNumber, logData, sanitizeText } from "../../../utils/genericFunctions";
import ValidationMessages from "../../../components/validationMessages/ValidationMessages";
import { motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToggleSwitch } from "../../../components/toogle/ToggleSwitch";
import { showToast } from "../../../toastService";
import { ImWhatsapp } from "react-icons/im";
import PdfPreview from "../../../components/pdfPreview/PdfPreview";
import { BsFillSendFill } from "react-icons/bs";
import moment from "moment";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";

const PAGE_SIZE = 9;

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const groupReportsByCategory = (reports = []) => {
    return reports.reduce((acc, report) => {
        const category = report?.rd_category?.trim() || "Uncategorized";
        if (!acc[category]) acc[category] = [];
        acc[category].push(report);
        return acc;
    }, {});
};

const CATEGORY_ORDER = [
    "FINANCE",
    "UNDERWRITING",
    "CLAIMS",
    "REINSURANCE",
    "HR",
    "PROCUREMENT",
    "GENERAL",
    "UNCATEGORIZED",
];

const sortGroupedCategories = (groupedReports = {}) => {
    return Object.entries(groupedReports)
        .sort(([categoryA], [categoryB]) => {
            const indexA = CATEGORY_ORDER.indexOf(categoryA);
            const indexB = CATEGORY_ORDER.indexOf(categoryB);

            const safeA = indexA === -1 ? 999 : indexA;
            const safeB = indexB === -1 ? 999 : indexB;

            if (safeA !== safeB) return safeA - safeB;
            return categoryA.localeCompare(categoryB);
        })
        .map(([category, reports]) => ({
            category,
            reports: reports.sort((a, b) =>
                String(a?.rd_name || "").localeCompare(String(b?.rd_name || ""))
            ),
        }));
};

const buildCategoryAwarePages = (
    groupedCategories = [],
    pageSize = 9,
    columns = 3
) => {
    const pages = [];
    let currentPage = [];
    let currentCount = 0;

    const pushCurrentPage = () => {
        if (currentPage.length > 0) {
            pages.push(currentPage);
            currentPage = [];
            currentCount = 0;
        }
    };

    groupedCategories.forEach((group) => {
        let reportIndex = 0;

        while (reportIndex < group.reports.length) {
            const reportsLeft = group.reports.length - reportIndex;
            let takeCount;

            if (currentCount === 0) {
                takeCount = Math.min(reportsLeft, pageSize);
            } else {
                const remainingToTarget = Math.max(0, pageSize - currentCount);
                takeCount = Math.min(reportsLeft, remainingToTarget);

                const remainder = takeCount % columns;
                const hasMoreInSameCategory = reportsLeft > takeCount;

                if (takeCount > 0 && hasMoreInSameCategory && remainder !== 0) {
                    const extraNeeded = columns - remainder;
                    const extraAvailable = reportsLeft - takeCount;
                    takeCount += Math.min(extraNeeded, extraAvailable);
                }
            }

            if (takeCount <= 0) {
                pushCurrentPage();
                continue;
            }

            const slice = group.reports.slice(reportIndex, reportIndex + takeCount);

            currentPage.push({
                category: group.category,
                reports: slice,
            });

            currentCount += slice.length;
            reportIndex += slice.length;

            if (currentCount >= pageSize) {
                pushCurrentPage();
            }
        }
    });

    pushCurrentPage();

    return pages;
};

const formatDateForInput = (value) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const parts = String(value).split(/[/-]/);
    if (parts.length === 3) {
        if (parts[2]?.length === 4) {
            const [dd, mmRaw, yyyy] = parts;
            const monthMap = {
                jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
                jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
            };
            const mm = monthMap[String(mmRaw).slice(0, 3).toLowerCase()] || mmRaw.padStart(2, "0");
            return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
        }
    }

    return "";
};

const formatDateForPayload = (value, format = null) => {
    console.log(`value ... ${value} ... format ${format}`);
    if (!value) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const [yyyy, mm, dd] = value.split("-");
    if (format) {
        const formattedToDate = moment(value).format(format)
        return formattedToDate;
    } else {
        return `${dd}/${mm}/${yyyy}`;
    }
};

const buildInitialFormValues = (parameters = []) => {
    const values = {};

    parameters.forEach((param) => {
        const valueSet = param?.value_set;
        const defaultValue = param?.default_resolved_value;

        if (valueSet?.vs_format_type === "DATE") {
            values[param.param_name] = formatDateForInput(defaultValue);
        } else {
            values[param.param_name] = defaultValue ?? "";
        }
    });

    return values;
};

const buildInitialSelectedOptions = (parameters = []) => {
    const options = {};

    parameters.forEach((param) => {
        const valueSet = param?.value_set;
        const defaultValue = param?.default_resolved_value;

        if (
            ["STATIC_LIST", "TABLE_SOURCE", "QUERY"].includes(valueSet?.vs_validation_type) &&
            defaultValue !== null &&
            defaultValue !== undefined &&
            defaultValue !== ""
        ) {
            options[param.param_name] = {
                value: defaultValue,
                label: String(defaultValue),
            };
        }
    });

    return options;
};

const AllReportRun = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const location = useLocation();
    const showBack = location.state?.showBack || "N";
    const [isLoading, setIsLoading] = useState(true);
    const [reportsList, setReportsList] = useState([]);
    const [selectedTab, setSelectedTab] = useState("all");
    const [currentView, setCurrentView] = useState("multiple");
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);

    const { group } = useParams();

    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");

    const [isProcessing, setIsProcessing] = useState(false);
    const [isProcessingTitle, setIsProcessingTitle] = useState(t('pleaseWait'));

    const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
    const [to, setTo] = useState("");
    const [message, setMessage] = useState("");
    const [charCount, setCharCount] = useState(0);

    const [validationMsgDialog, setValidationMsgDialog] = useState(false);
    const [validationMessagesResult, setValidationMessagesResult] = useState({});

    const [selectedReport, setSelectedReport] = useState(null);
    const [reportMeta, setReportMeta] = useState(null);
    const [isMetaLoading, setIsMetaLoading] = useState(false);
    const [isRunning, setIsRunning] = useState(false);

    const [formValues, setFormValues] = useState({});
    const [selectedOptions, setSelectedOptions] = useState({});
    const [outputFormat, setOutputFormat] = useState("PDF");
    const [background, setBackground] = useState("N");
    const [email, setEmail] = useState("N");
    const [emailList, setEmailList] = useState("");
    const [executableOutputFormats, setExecutableOutputFormats] = useState([]);

    const [pdfPreviewDialogOpen, setPdfPreviewDialogOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [binaryDocid, setBinaryDocid] = useState(null);



    const fetchReportKeys = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.post("/reportRun/findAll", { group });
            // logData("reportsList",res.data);
            setReportsList(res.data || []);
        } catch (error) {
            console.error("Error fetching reports:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleContentChange = (e) => {
        const value = e.target.value;
        setMessage(value);
        setCharCount(value.length);
    };

    const handleDocumentShare = async () => {
        try {

            if (!to) {
                setNotifyDialogTitle("Note!");
                setNotifyDialogText(`Please specify valid WhatsApp Number!`);
                setIsNotifyDialogOpen(true);
                return false;
            } else {
                if (!isValidMobileNumber(to)) {
                    setNotifyDialogTitle("Note!");
                    setNotifyDialogText(`Please specify valid WhatsApp Number!`);
                    setIsNotifyDialogOpen(true);
                    return false;
                }
            }

            if (!message) {
                setNotifyDialogTitle("Note!");
                setNotifyDialogText(`Please enter message!`);
                setIsNotifyDialogOpen(true);
                return false;
            }

            setIsProcessingTitle(t('sharingTo', { param1: to }));
            setIsProcessing(true);

            const res = await newRequest.post("/exportFiles/sendReportToTwilio", {
                report_id: binaryDocid,
                to,
                message
            });

            setIsProcessing(false);

            if (res.data.msg === "sent") {
                setMessage("");
                setWhatsAppDialogOpen(false);
                showToast(t('sharedWith', { param1: to }), "success");
            } else {
                console.error("Error details:", res.data?.error);
                showToast("error sharing the file!", "error");
            }

        } catch (error) {
            setIsProcessing(false);
            console.error("Error sharing document:", error);
        }
    };

    const filteredReports = useMemo(() => {
        const term = normalizeText(searchTerm);

        if (!term) return reportsList || [];

        return (reportsList || []).filter((item) => {
            const category = normalizeText(item?.rd_category);
            const name = normalizeText(item?.rd_name);
            const ref = normalizeText(item?.rd_ref);
            const description = normalizeText(item?.rd_description);

            return (
                category.includes(term) ||
                name.includes(term) ||
                ref.includes(term) ||
                description.includes(term)
            );
        });
    }, [reportsList, searchTerm]);

    const groupedFilteredReports = useMemo(() => {
        const grouped = groupReportsByCategory(filteredReports);
        return sortGroupedCategories(grouped);
        // return grouped;
    }, [filteredReports]);

    const categoryTotalsMap = useMemo(() => {
        const totals = {};
        groupedFilteredReports.forEach((group) => {
            totals[group.category] = group.reports.length;
        });
        return totals;
    }, [groupedFilteredReports]);

    const categoryReportPositionMap = useMemo(() => {
        const map = {};
        groupedFilteredReports.forEach((group) => {
            group.reports.forEach((report, index) => {
                map[report._id] = index + 1;
            });
        });
        return map;
    }, [groupedFilteredReports]);

    const pagedCategoryGroups = useMemo(() => {
        return buildCategoryAwarePages(groupedFilteredReports, PAGE_SIZE);
    }, [groupedFilteredReports]);

    const totalReports = filteredReports.length;
    const totalPages = Math.max(1, pagedCategoryGroups.length || 1);

    const currentPageGroups = useMemo(() => {
        return pagedCategoryGroups[page - 1] || [];
    }, [pagedCategoryGroups, page]);



    const handlePrevPage = () => {
        setPage((prev) => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setPage((prev) => Math.min(totalPages, prev + 1));
    };

    const fetchReportMeta = async (report) => {
        try {
            setIsMetaLoading(true);
            const res = await newRequest.get(`/reportRun/find/${report._id}`);
            // logData("singleReport", res.data);
            const meta = res.data;
            setSelectedReport(report);
            setReportMeta(meta);
            setFormValues(buildInitialFormValues(meta?.parameters || []));
            setSelectedOptions(buildInitialSelectedOptions(meta?.parameters || []));
            setOutputFormat(meta?.report?.rd_output_type || "PDF");
            setCurrentView("single");
        } catch (error) {
            console.error("Error fetching report meta:", error);
        } finally {
            setIsMetaLoading(false);
        }
    };

    const clearDependents = useCallback((changedParamName, parameters) => {
        const allDependents = (parameters || [])
            .filter((param) => (param.dependencies || []).includes(changedParamName))
            .map((param) => param.param_name);

        if (allDependents.length === 0) return;

        setFormValues((prev) => {
            const updated = { ...prev };
            allDependents.forEach((name) => {
                updated[name] = "";
            });
            return updated;
        });

        setSelectedOptions((prev) => {
            const updated = { ...prev };
            allDependents.forEach((name) => {
                delete updated[name];
            });
            return updated;
        });

        allDependents.forEach((depName) => clearDependents(depName, parameters));
    }, []);

    const handleOpenReport = async (report) => {
        await fetchReportMeta(report);
    };

    const handleBackToList = () => {
        setCurrentView("multiple");
        setSelectedReport(null);
        setReportMeta(null);
        setEmail("N");
        setBackground("N");
        setFormValues({});
        setSelectedOptions({});
    };

    const handleInputChange = (param, value) => {
        setFormValues((prev) => ({
            ...prev,
            [param.param_name]: value,
        }));

        clearDependents(param.param_name, reportMeta?.parameters || []);
    };

    const handleSelectChange = (param, option) => {
        setSelectedOptions((prev) => ({
            ...prev,
            [param.param_name]: option || null,
        }));

        setFormValues((prev) => ({
            ...prev,
            [param.param_name]: option?.value ?? "",
        }));

        clearDependents(param.param_name, reportMeta?.parameters || []);
    };

    const loadAsyncOptions = async (param, inputValue) => {
        try {
            const res = await newRequest.post(`/reportRun/params/${selectedReport?._id}`, {
                parameterName: param.param_name,
                formValues,
                search: inputValue || "",
                page: 1,
                limit: 20,
            });

            if (res?.data?.waitingForDependencies) {
                return [];
            }

            return (res?.data?.options || []).map((item) => ({
                value: item.code,
                label: item.label,
            }));
        } catch (error) {
            console.error("Error loading async options:", error);
            return [];
        }
    };

    const getStaticOptions = (param) => {
        const values = param?.value_set?.vs_static_values || [];
        return values.map((item) => ({
            value: item.code,
            label: item.label,
        }));
    };

    const buildRunPayload = () => {
        const payload = {};
        let msgList = [];
        let msgIndex = 0;
        (reportMeta?.parameters || []).forEach((param) => {
            const valueSet = param?.value_set;
            let value = formValues?.[param.param_name];

            if (valueSet?.vs_format_type === "DATE") {
                value = formatDateForPayload(value, valueSet?.vs_format_mask);
            }

            payload[param.param_name] = value === "" ? null : value;

            if (param.param_required === "Y" && !value) {
                msgIndex++;
                msgList.push({
                    msg_index: msgIndex,
                    msg_no: `REQ${msgIndex}`,
                    msg_title: `${param.param_label}`,
                    msg_text: `${t('parameter')} ${param.param_label?.toUpperCase()} is ${t('mandatory')}`,
                    msg_type: "missing_information",
                });
            }

        });

        payload.P_OUTPUT_FORMAT = outputFormat;

        return { payload, msgList };
    };

    const handleRunReport = async () => {
        try {
            if (email === "Y" && !emailList) {
                setNotifyDialogTitle(t('error'));
                setNotifyDialogText(t('specifyEmailList'));
                setIsNotifyDialogOpen(true);
                return;
            }

            setIsRunning(true);
            const { payload, msgList } = buildRunPayload();

            // logData("payload", payload);
            // logData("msgList", msgList);

            if (msgList && Array.isArray(msgList) && msgList.length > 0) {
                setValidationMessagesResult({
                    result_status: "error",
                    msg_title: `${selectedReport?.rd_name} exceptions`,
                    result_msg: `Some exceptions occured while submitting ${selectedReport?.rd_name}`,
                    result_msg_list: msgList,
                });
                setValidationMsgDialog(true);
                return;
            } else {
                // const res = await newRequest.post(`/reportRun/run/${selectedReport?._id}`, { reportParameters: payload });
                // const res = await newRequest.post("gendocs/agenda-reps", {
                //     report_docid: selectedReport?._id,
                //     report_parameters: payload
                // });
                // logData("res", res.data);

                try {
                    setIsProcessingTitle(t('runningReport', { param1: selectedReport?.rd_name }));
                    setIsProcessing(true);
                    const docRes = await newRequest.post(
                        `/gendocs/${selectedReport?.rd_endpoint}`,
                        {
                            report_docid: selectedReport?._id,
                            report_parameters: payload,
                            background,
                            email,
                            emailList
                        },
                        {
                            responseType: 'json',
                        }
                    );

                    // console.log(`0 docRes\n${JSON.stringify(docRes.data, null, 2)}`);

                    const data = docRes.data;
                    console.log(`data?.job_status ... ${data?.job_status} ... ${background}`);
                    setIsProcessing(false);

                    // Background mode
                    if (data?.job_status === "queued") {
                        setIsProcessing(false);
                        setNotifyDialogTitle(t("note"));
                        setNotifyDialogText(background === "Y" ? t('reportGenBackground') : t('reportGenConvBackground'));
                        setIsNotifyDialogOpen(true);
                        return; // skip file handling
                    }

                    // Error mode
                    if (data?.job_status === "error") {
                        setIsProcessing(false);
                        setNotifyDialogTitle("Error");
                        setNotifyDialogText(data.job_msg || "Report generation failed");
                        setIsNotifyDialogOpen(true);
                        return;
                    }

                    // console.log("Document generated successfully");

                    // Foreground mode: decode base64 buffer
                    if (data?.buffer && data?.mimeType) {
                        setIsProcessing(false);
                        setBinaryDocid(data.docId);

                        const byteArray = Uint8Array.from(atob(data.buffer), c => c.charCodeAt(0));
                        const blob = new Blob([byteArray], { type: data.mimeType });
                        const url = URL.createObjectURL(blob);

                        if (data.mimeType.toLowerCase().startsWith("application/pdf")) {
                            setPdfUrl(url);
                            setPdfPreviewDialogOpen(true);
                        } else if (data.mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = data.fileName || selectedReport?.rd_name.replace(".docx", ".xlsx");
                            link.click();
                            URL.revokeObjectURL(url);
                        }
                        // console.log("saved doc_id:", data.docId);
                    } else {
                        throw new Error("No buffer/mimeType in response");
                    }
                } catch (error) {
                    setIsProcessing(false);
                    console.log("error:", error);
                    if (error.response?.status === 404) {
                        setNotifyDialogTitle("Error");
                        setNotifyDialogText(`Endpoint not setup for ${selectedReport?.rd_name}`);
                    } else if (error.response) {
                        setNotifyDialogTitle("Error");
                        setNotifyDialogText(`Error: ${error.response.status} - ${error.response.statusText}`);
                    } else if (error.request) {
                        setNotifyDialogTitle("Error");
                        setNotifyDialogText("No response from server. Please check your network.");
                    } else {
                        setNotifyDialogTitle("Error");
                        setNotifyDialogText(`Request error: ${error.message}`);
                    }
                    setIsNotifyDialogOpen(true);
                }
            }
        } catch (error) {
            console.error("Error running report:", error);
        } finally {
            setIsRunning(false);
        }
    };

    const renderParameterField = (param) => {
        const valueSet = param?.value_set;
        const validationType = valueSet?.vs_validation_type;
        const formatType = valueSet?.vs_format_type;
        const selectedOption = selectedOptions?.[param.param_name] || null;
        const currentValue = formValues?.[param.param_name] ?? "";

        if (param?.param_is_hidden === "Y") return null;

        if (validationType === "STATIC_LIST") {
            return (
                <Select
                    menuPortalTarget={document.body}
                    styles={{
                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                        menuPortal: base => ({
                            ...base,
                            zIndex: 9999,
                        })
                    }}
                    width="350px"
                    isSearchable={true}
                    options={getStaticOptions(param)}
                    value={selectedOption}
                    onChange={(option) => handleSelectChange(param, option)}
                    isClearable={!param?.param_required === "Y"}
                    placeholder={`${t("select")} ${param.param_label}`}
                />
            );
        }

        if (validationType === "TABLE_SOURCE" || validationType === "QUERY") {
            return (
                <AsyncSelect
                    menuPortalTarget={document.body}
                    styles={{
                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                        menuPortal: base => ({
                            ...base,
                            zIndex: 9999,
                        })
                    }}
                    width="350px"
                    isSearchable={true}
                    noOptionsMessage={() => "Type Minimum 1 Letter to Search"}
                    cacheOptions
                    defaultOptions
                    loadOptions={(inputValue) => loadAsyncOptions(param, inputValue)}
                    value={selectedOption}
                    onChange={(option) => handleSelectChange(param, option)}
                    isClearable={param?.param_required === "N"}
                    placeholder={`${t("select")} ${param.param_label}`}
                />
            );
        }

        if (validationType === "FIXED_VALUE") {
            if (formatType === "DATE") {
                return (
                    <div className="input-item-with-label">
                        <input
                            type="date"
                            className="form-input-item"
                            style={{ width: "350px" }}
                            value={currentValue}
                            onChange={(e) => handleInputChange(param, e.target.value)}
                        />
                    </div>
                );
            }

            if (formatType === "NUMBER") {
                return (
                    <div className="input-item-with-label">
                        <input
                            type="number"
                            className="form-input-item"
                            style={{ width: "350px" }}
                            value={currentValue}
                            min={valueSet?.vs_min_value ?? undefined}
                            max={valueSet?.vs_max_value ?? undefined}
                            onChange={(e) => handleInputChange(param, e.target.value)}
                        />
                    </div>
                );
            }

            return (
                <div className="input-item-with-label">
                    <input
                        type="text"
                        className="form-input-item"
                        style={{ width: "350px" }}
                        value={currentValue}
                        onChange={(e) => handleInputChange(param, e.target.value)}
                    />
                </div>
            );
        }

        return (
            <div className="input-item-with-label">
                <input
                    type="text"
                    className="form-input-item"
                    value={currentValue}
                    onChange={(e) => handleInputChange(param, e.target.value)}
                />
            </div>
        );
    };

    useEffect(() => {
        if (selectedReport?.rd_type === "Report") {
            setExecutableOutputFormats(["PDF", "EXCEL"]);
        } else {
            setExecutableOutputFormats(["PDF", "EXCEL", "CSV", "JSON", "NONE"]);
        }
    }, [selectedReport]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, reportsList, selectedTab]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    useEffect(() => {
        fetchReportKeys();
    }, [group]);


    return (
        <div className={`all-report-run ${currentTheme === "dark" ? "dark" : "light"}`}>
            {(isLoading || isMetaLoading) ? (
                <div className="ar__loading">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="ar__shell">
                    <main className="ar__main">
                        <div className="ar__content">
                            {currentView === "multiple" ? (
                                <>
                                    <div className="ar__hero">
                                        <div className="ar__heroTitleBar">
                                            <div className="page-title">{t("reports")}</div>
                                            <div className="ar__tabs">
                                                <button
                                                    onClick={() => setSelectedTab("all")}
                                                    className={selectedTab === "all" ? "ar__tab current" : "ar__tab"}
                                                >
                                                    All Reports
                                                </button>

                                                <button
                                                    onClick={() => setSelectedTab("fav")}
                                                    className={selectedTab === "fav" ? "ar__tab current" : "ar__tab"}
                                                >
                                                    Favourites
                                                </button>

                                                <button
                                                    onClick={() => setSelectedTab("quick")}
                                                    className={selectedTab === "quick" ? "ar__tab current" : "ar__tab"}
                                                >
                                                    Quick Access
                                                </button>
                                                {/* This part shows if the menu was opened from home screen */}
                                                {showBack === "Y" && <hr className="separator-v" />}
                                                {showBack === "Y" &&
                                                    <CustomLoadingButton
                                                        currentTheme={currentTheme}
                                                        iconPosition="end"
                                                        borderRadiusType="both"
                                                        variant="tint"
                                                        color="#f55656"
                                                        endIcon={<MdClose />}
                                                        onClick={() => navigate(-1)}>
                                                        {t('close')}
                                                    </CustomLoadingButton>}
                                            </div>

                                        </div>
                                    </div>

                                    <div className="ar__filters">
                                        <div className="homeSearch">
                                            <FiSearch className="homeSearchIcon" />
                                            <input
                                                className="homeSearchInput"
                                                type="text"
                                                placeholder={`${t("search")} ${t("report")}...`}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            {searchTerm?.length > 0 ? (
                                                <MdClose
                                                    className="homeSearchClear"
                                                    onClick={() => setSearchTerm("")}
                                                />
                                            ) : (
                                                <span className="homeSearchClearPlaceholder" />
                                            )}
                                        </div>
                                    </div>

                                    <div className="ar__body">
                                        {currentPageGroups.length === 0 ? (
                                            <div className="ar__empty">
                                                No reports found.
                                            </div>
                                        ) : (
                                            currentPageGroups.map((group) => (
                                                <div key={group.category} className="ar__categoryBlock">
                                                    <div className="ar__categoryHeader">
                                                        <div className="ar__categoryTitle">{group.category}</div>
                                                        <div className="ar__categoryCount">
                                                            {categoryTotalsMap[group.category] || 0} report
                                                            {(categoryTotalsMap[group.category] || 0) === 1 ? "" : "s"}
                                                        </div>
                                                    </div>

                                                    <div className="ar__gridWrap">
                                                        {group.reports.map((report) => {
                                                            const IconComponent =
                                                                qilloIconMap?.[report?.rd_icon] || Home;

                                                            const categoryIndex = categoryReportPositionMap[report._id] || 0;

                                                            return (
                                                                <div
                                                                    key={report?._id}
                                                                    className="ar__card"
                                                                    onClick={() => handleOpenReport(report)}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            handleOpenReport(report);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="ar__cardNum">
                                                                        {String(categoryIndex).padStart(2, "0")}
                                                                    </div>

                                                                    <div className="ar__cardIcon">
                                                                        <IconComponent className="ar__cardIconSvg" />
                                                                    </div>

                                                                    <div className="ar__cardTitle">{report?.rd_name}</div>
                                                                    <div className="ar__cardDesc">{report?.rd_description}</div>

                                                                    <div className="ar__cardFooter">
                                                                        <div className="ar__cardId">
                                                                            ID: #{report?.rd_ref || report?._id?.slice(-6)}
                                                                        </div>
                                                                        <div className="ar__cardRun">
                                                                            Run Report <span className="ar__arrow">→</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))
                                        )}

                                        <div className="ar__pagination">
                                            <button
                                                className="ar__pageBtn"
                                                onClick={handlePrevPage}
                                                disabled={page === 1}
                                            >
                                                {t("prev")}
                                            </button>

                                            <div className="ar__pageInfo">
                                                Page {page} of {totalPages} · {totalReports} report
                                                {totalReports === 1 ? "" : "s"}
                                            </div>

                                            <button
                                                className="ar__pageBtn"
                                                onClick={handleNextPage}
                                                disabled={page === totalPages}
                                            >
                                                {t("next")}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="ar__single">
                                    <div className="ar__singleHeader">

                                        <div className="ar__singleTitleWrap">
                                            <div className="page-title">{selectedReport?.rd_name}</div>
                                        </div>

                                        <CustomLoadingButton
                                            currentTheme={currentTheme}
                                            iconPosition="end"
                                            borderRadiusType="both"
                                            variant="tint"
                                            color="#1E88E5"
                                            tooltip="Back to reports list"
                                            endIcon={<ArrowLeft />}
                                            onClick={handleBackToList}>
                                            {t('back')}
                                        </CustomLoadingButton>
                                    </div>

                                    <div className="ar__paramCard">
                                        <div className="ar__singleSubTitle">
                                            {selectedReport?.rd_ref || selectedReport?.rd_code}
                                        </div>
                                        <div className="ar__paramGrid">
                                            {(reportMeta?.parameters || []).map((param) => (
                                                <div key={param._id || param.param_name} className="ar__paramRow">
                                                    <label className="ar__paramLabel">
                                                        {param.param_label?.toUpperCase()}
                                                        {param?.param_required === "Y" ? " *" : ""}
                                                    </label>
                                                    <div className="ar__paramField">
                                                        {renderParameterField(param)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="ar__formatRow">
                                            <label className="ar__paramLabel">{t('format').toUpperCase()}</label>
                                            <CustomRadioGroup
                                                id="hd_type"
                                                value={outputFormat}
                                                onChange={(next) => setOutputFormat(next)}
                                                label={outputFormat}
                                                labelPosition="end"
                                                labelWidth="max-content"
                                                options={executableOutputFormats.map((v) => ({ value: v, label: v }))}
                                            />
                                        </div>

                                        <div className="ar__formatRow">
                                            <label className="ar__paramLabel">{t('background').toUpperCase()}</label>
                                            <ToggleSwitch
                                                id="background"
                                                value={background}
                                                onChange={(val) => {
                                                    try {
                                                        if (email === "Y") {
                                                            setNotifyDialogTitle(t('note'));
                                                            setNotifyDialogText(t('backgroundEmail'));
                                                            setIsNotifyDialogOpen(true);
                                                            setBackground("Y");
                                                        } else {
                                                            setBackground(val);
                                                        }
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                                label={""}
                                                labelWidth="max-content"
                                                labelPosition="start"
                                                border="N"
                                                style={{ width: "max-content" }}
                                            />
                                        </div>
                                        {/* <div className="ar__formatRow">
                                            <label className="ar__paramLabel">{t('email').toUpperCase()}</label>
                                            <ToggleSwitch
                                                id="email"
                                                value={email}
                                                onChange={(val) => setEmail(val)}
                                                label={""}
                                                labelWidth="max-content"
                                                labelPosition="start"
                                                border="N"
                                                style={{ width: "max-content" }}
                                            />
                                        </div> */}

                                        <div className="ar__formatRow">
                                            <label className="ar__paramLabel">{t('email').toUpperCase()}</label>
                                            <div className="input-item-with-label">
                                                <ToggleSwitch
                                                    id="email"
                                                    value={email}
                                                    onChange={(val) => {
                                                        try {
                                                            setEmail(val);
                                                            if (val === "Y") {
                                                                setBackground("Y");
                                                                setEmailList(currentUser?.usr_email);
                                                            } else {
                                                                setEmailList(null);
                                                            }
                                                        } catch (error) {
                                                            console.log(error);
                                                        }
                                                    }}
                                                    label={""}
                                                    labelWidth="max-content"
                                                    labelPosition="start"
                                                    border="N"
                                                    style={{ width: "max-content" }}
                                                />
                                                {email === "Y" &&
                                                    <input
                                                        name="emailList"
                                                        type="text"
                                                        placeholder={t('list').toUpperCase()}
                                                        className="form-input-item"
                                                        style={{ width: "280px" }}
                                                        value={emailList}
                                                        onChange={(e) => setEmailList(e.target.value)}
                                                    />}
                                            </div>
                                        </div>

                                        <div className="ar__runWrap">
                                            <CustomLoadingButton
                                                currentTheme={currentTheme}
                                                iconPosition="end"
                                                borderRadiusType="both"
                                                width={"350px"}
                                                variant="tint"
                                                color="#1E88E5"
                                                tooltip={`${t('run')} ${selectedReport?.rd_name}`}
                                                endIcon={<GoArrowRight />}
                                                onClick={handleRunReport}
                                                disabled={isRunning}>
                                                {isRunning ? t('running') : t('run')}
                                            </CustomLoadingButton>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            )}
            {validationMsgDialog && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-content">
                            <ValidationMessages
                                messageData={validationMessagesResult}
                                onClose={() => setValidationMsgDialog(false)}
                                onFix={(row) => {/* focus field / navigate */ }}
                                onRemind={() => setValidationMsgDialog(false)}
                                onSupport={() => navigate("/support")}
                            />
                        </div>
                    </div>
                </motion.div>
            )}
            {pdfPreviewDialogOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper pdf">
                        <div className="overlay-navbar">
                            <div className="left">
                                <h1 className="main-title">{t('preview').toUpperCase()}</h1>
                            </div>
                            <div className="right" style={{ gap: "5px" }}>
                                {binaryDocid &&
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        iconPosition="end"
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#E040FB"
                                        tooltip="Share on WhatsApp"
                                        endIcon={<ImWhatsapp />}
                                        onClick={() => setWhatsAppDialogOpen(true)}>
                                        {t('share')}
                                    </CustomLoadingButton>}
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="both"
                                    variant="tint"
                                    color="#7CB342"
                                    tooltip="Send to email"
                                    endIcon={<MdAlternateEmail />}
                                    onClick={() => {
                                        // try {
                                        //     const urlString = pdfUrl.toString().replace("blob:", "");
                                        //     setEmailData({
                                        //         to: documentData?.clientEmail,
                                        //         from: currentUser?.usr_org_name,
                                        //         cc: '',
                                        //         bcc: '',
                                        //         subject: `${documentData?.docType} No ${documentData?.docNo}`,
                                        //         show_attach: "N",
                                        //         attached_urls: [{ id: 1, url: urlString, name: `${documentData?.docType} No ${documentData?.docNo}`, binary_docid: binaryDocid }],
                                        //         binary_docs: [{ doc_id: binaryDocid, doc_name: `${documentData?.docType} No ${documentData?.docNo}`, doc_type: "pdf", doc_url: "", doc_size_kb: 0 }],
                                        //         doc_no: documentData?.docNo,
                                        //         doc_type: documentData?.docType,
                                        //         doc_id: documentData?.docId,
                                        //         entity_codes: [documentData?.clientEntCode],
                                        //         search_string: documentData?.clientEntName + "," + documentData?.docNo,
                                        //         ...documentData
                                        //     });
                                        //     setPdfPreviewDialogOpen(false);
                                        //     setEmailDialogOpen(true);
                                        // } catch (error) {
                                        //     console.log(error)
                                        // }
                                    }}>
                                    {t('email')}
                                </CustomLoadingButton>
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="both"
                                    variant="tint"
                                    color="#00BCD4"
                                    tooltip="Download report"
                                    endIcon={<MdDownload />}
                                    onClick={() => {
                                        try {
                                            // const link = document.createElement('a');
                                            // link.href = pdfUrl;
                                            // link.download = `${documentData?.docType} ${documentData?.docNo}.pdf`;
                                            // document.body.appendChild(link);
                                            // link.click();
                                            // document.body.removeChild(link);
                                        } catch (error) {
                                            console.log(error)
                                        }
                                    }}>
                                    {t('download')}
                                </CustomLoadingButton>
                                <hr className='separator-v' />
                                <CircularButton
                                    currentTheme={currentTheme}
                                    onClick={() => setPdfPreviewDialogOpen(false)}
                                    icon={<MdClose />}
                                    width={"35px"}
                                    height={"35px"} />
                            </div>
                        </div>
                        <div className="overlay-content-pdf" >
                            <PdfPreview pdfUrl={pdfUrl} />
                        </div>
                        <div className="overlay-actions">
                        </div>
                    </div>
                </motion.div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <span className='main-title'>{isProcessingTitle || t('pleaseWait')}</span>
                        <ScaleLoader
                            color="var(--primary)"
                            height={50}
                            radius={8}
                            width={4}
                        />
                    </div>
                </motion.div>)}
            {whatsAppDialogOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <div className="left">
                                <span className="main-title">{t("shareToWhatsApp")}</span>
                            </div>
                            <div className="right">
                                <CircularButton currentTheme={currentTheme} onClick={() => setWhatsAppDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                            </div>
                        </div>
                        <div className="overlay-content">
                            <div className="input-item-with-label">
                                <input
                                    id="to"
                                    name="to"
                                    type="text"
                                    placeholder={t("to")}
                                    value={to}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    onChange={(e) => setTo(e.target.value)}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <textarea
                                    id="message"
                                    name="message"
                                    type="text"
                                    placeholder={t("message")}
                                    value={message}
                                    rows={3}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    onChange={handleContentChange}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleDocumentShare();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <span>{charCount} {t("characters")}</span>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                onClick={handleDocumentShare}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#F50057"
                                tooltip={`Share document to ${to}`}
                                endIcon={<BsFillSendFill />}
                            >
                                {t("share")}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>
            )}
            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: sanitizeText(notifyDialogText) }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton currentTheme={currentTheme} variant="tint" color="#E040FB" onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AllReportRun;